import { NOT_VISIBLE } from 'cvent-question-widgets/lib/DisplayType';
import StandardContactFields from 'event-widgets/lib/StandardContactFields/StandardContactFields';
import getJSONValue from 'nucleus-widgets/utils/fields/getJSONValue';
import { SAVING_REGISTRATION } from '../../registrationIntents';
import { UPDATE_REG_CART_FAILURE, UPDATE_REG_CART_PENDING, UPDATE_REG_CART_SUCCESS } from './actionTypes';
import { getIn, setIn, updateIn } from 'icepick';
import { setAdminRegOnProductionSelection } from './internal';
import { updateGuestsToMatchPrimaryReg } from './guests';
import { filterEventSnapshot } from '../../actions';
import { getRegPackId } from '../../selectors/shared';
import { updateRegTypeAndAdmissionItemIdsInTravelBookings } from '../../travelCart';
import { clearInapplicableSelectedPaymentMethod } from '../regCartPayment/actions';
import { getAllSessionCapacityIds, loadCapacity } from 'event-widgets/redux/modules/capacity';
import { calculatePricing } from './pricing';
import { loadAvailableCapacityCounts } from '../../capacity';
import { hideLoadingOnError } from 'nucleus-guestside-site/src/redux/modules/dialogContainer';
import { getUpdateErrors } from '../errors';
import { openCapacityReachedDialog, openPrivateEventErrorDialog } from '../../../dialogs';
import Logger from '@cvent/nucleus-logging';
import { pickBy, set, merge, isEmpty } from 'lodash';

const LOG = new Logger('redux/registrationForm/regCart/partialUpdates');

const SESSION_THRESHOLD = 100;

function getVisibleFieldValues(state, personalInformation, regPathId) {
  const regPath = state.appData.registrationSettings.registrationPaths[regPathId];
  const regFields = regPath.registrationPageFields[1].registrationFields;
  const customFields = pickBy(personalInformation.customFields, (field, fieldId) => {
    return regFields[fieldId] && regFields[fieldId].display !== NOT_VISIBLE;
  });
  const result = { customFields };
  Object.keys(StandardContactFields).forEach(fieldId => {
    if (regFields[fieldId] && regFields[fieldId].display !== NOT_VISIBLE) {
      const path = StandardContactFields[fieldId].regApiPath;
      const value = getJSONValue(personalInformation, path);
      if (value) {
        set(result, path, value);
      }
    }
  });
  return result;
}

function getVisibleQuestionAnswers(state, eventAnswers, regPathId) {
  const { registrationQuestions, productQuestions } = state.appData.registrationSettings;
  return pickBy(eventAnswers, (answer, questionId) => {
    return (registrationQuestions[questionId] &&
      registrationQuestions[questionId].registrationPathQuestionAssociations.includes(regPathId)) ||
      (productQuestions[questionId] &&
        productQuestions[questionId].registrationPathQuestionAssociations.includes(regPathId));
  });
}

function getVisibleFieldsAndQuestionValues(state, attendee, regPathId) {
  return {
    personalInformation: getVisibleFieldValues(state, attendee.personalInformation, regPathId),
    eventAnswers: getVisibleQuestionAnswers(state, attendee.eventAnswers, regPathId)
  };
}

function getTermsAndConditionsValues(state, attendee, regPathId) {
  const regPath = state.appData.registrationSettings.registrationPaths[regPathId];
  const termsConditionsEnabled = regPath.termsConditionsEnabled;
  if (termsConditionsEnabled) {
    return {
      termsAndConditionsAccepted: attendee.termsAndConditionsAccepted
    };
  }
  return {};
}

function getAttendeeFieldValues(state, attendee, regPathId) {
  return merge(getVisibleFieldsAndQuestionValues(state, attendee, regPathId),
    getTermsAndConditionsValues(state, attendee, regPathId)
  );
}

/**
 * Selectively update requested fields in RegCart:
 * 1. registrationType, 2. productRegistrations, 3. sessionRegistrations
 * These product selection requires immediate server side update (outside of automatic save on page navigation)
 */
export function applyPartialEventRegistrationUpdate(eventRegistrationId, eventRegUpdates,
  guestEventRegUpdates, productId, isSessionSelectionUpdate) {
  return async (dispatch, getState) => {
    LOG.debug('applyPartialEventRegistrationUpdate', eventRegistrationId, eventRegUpdates);
    const { accessToken, registrationForm: { regCart: cart },
      regCartStatus: { lastSavedRegCart, registrationIntent },
      clients: { regCartClient },
      event } = getState();
    if (registrationIntent === SAVING_REGISTRATION) {
      return;
    }
    dispatch({ type: UPDATE_REG_CART_PENDING });
    const lastSavedAttendee = getIn(lastSavedRegCart, ['eventRegistrations', eventRegistrationId, 'attendee']);
    const currentVoucher = getIn(cart, ['eventRegistrations', eventRegistrationId, 'appliedVoucher']);
    const registrationStatus = getIn(cart, ['eventRegistrations', eventRegistrationId, 'registrationStatus']);
    let cartWithSelection = updateIn(cart, ['eventRegistrations', eventRegistrationId],
      eventReg => {
        let appliedVoucher = {};
        if (!!currentVoucher && !isEmpty(currentVoucher)) {
          appliedVoucher = { appliedVoucher: {} };
          // The voucher must be included (if present) or capacity will be reserved as if it is a newly used voucher
          // during reg mod
          if (registrationStatus === 'REGISTERED') {
            appliedVoucher = { appliedVoucher: currentVoucher };
          }
        }
        return {
          ...eventReg,
          ...eventRegUpdates,
          attendee: lastSavedAttendee,
          ...appliedVoucher
        };
      });
    // Remove admin infomation from regcart with updates
    cartWithSelection = setAdminRegOnProductionSelection(lastSavedRegCart, cartWithSelection);

    /* Simple guests take the same products as the primary always. Complex guests can have diffent products. */
    if (guestEventRegUpdates) {
      Object.keys(guestEventRegUpdates).forEach((updatedGuestRegId) => {
        cartWithSelection = updateIn(cartWithSelection, ['eventRegistrations', updatedGuestRegId],
          currentGuestReg => {
            return {
              ...currentGuestReg,
              ...guestEventRegUpdates[updatedGuestRegId]
            };
          });
      });
    }

    // Don't match sessions or admission items for complex guests
    cartWithSelection = updateGuestsToMatchPrimaryReg(cartWithSelection, eventRegistrationId, getState(), productId);

    try {
      LOG.debug('updateRegCart', cartWithSelection);
      const response = await regCartClient.updateRegCart(accessToken, cartWithSelection);
      LOG.debug('updateRegCart success');
      const savedRegCart = response.regCart;
      const { registrationTypeId: newRegTypeId, registrationPathId: newRegPathId } = getIn(
        savedRegCart, ['eventRegistrations', eventRegistrationId]);
      const { registrationTypeId, registrationPathId } = getIn(
        lastSavedRegCart, ['eventRegistrations', eventRegistrationId]);
      if (registrationTypeId !== newRegTypeId || registrationPathId !== newRegPathId) {
        await dispatch(filterEventSnapshot(getState().eventSnapshotVersion, newRegTypeId, newRegPathId));
      }
      // Put the information that wasn't saved as part of selecting a product back in the reg cart
      const notSavedAttendee = getAttendeeFieldValues(getState(),
        getIn(cart, ['eventRegistrations', eventRegistrationId, 'attendee']),
        getIn(savedRegCart, ['eventRegistrations', eventRegistrationId, 'registrationPathId'])
      );
      let regCartWithUnsavedChanges = setIn(savedRegCart, ['eventRegistrations', eventRegistrationId, 'attendee'],
        notSavedAttendee);
      if (guestEventRegUpdates) {
        Object.keys(guestEventRegUpdates)
          .filter((eventRegId) => eventRegId !== eventRegistrationId)
          .forEach((guestEventRegId) => {
            const notSavedGuest = getAttendeeFieldValues(getState(),
              getIn(cart, ['eventRegistrations', guestEventRegId, 'attendee']),
              getIn(savedRegCart, ['eventRegistrations', guestEventRegId, 'registrationPathId'])
            );
            regCartWithUnsavedChanges = setIn(regCartWithUnsavedChanges, ['eventRegistrations', guestEventRegId, 'attendee'],
              notSavedGuest);
          });
      }
      const notSavedVoucher = getIn(cart, ['eventRegistrations', eventRegistrationId, 'appliedVoucher']);
      if (!isEmpty(notSavedVoucher)) {
        regCartWithUnsavedChanges = setIn(regCartWithUnsavedChanges,
          ['eventRegistrations', eventRegistrationId, 'appliedVoucher'],
          notSavedVoucher);
      }
      // Put back unsaved Admin information
      regCartWithUnsavedChanges = setAdminRegOnProductionSelection(cart, regCartWithUnsavedChanges);
      dispatch({
        type: UPDATE_REG_CART_SUCCESS, payload: {
          regCart: regCartWithUnsavedChanges,
          savedRegCart,
          validationMessages: response.validationMessages
        }
      });
      // Update the reg type ids and admission item ids in the travel cart if anything has changed
      await dispatch(updateRegTypeAndAdmissionItemIdsInTravelBookings());
      LOG.debug('loadAvailableCapacityCounts,calculatePricing');

      // Allowed payment methods might have changed, clear the selected payment method if it is no longer allowed
      dispatch(clearInapplicableSelectedPaymentMethod());

      // Due to performance degradation for events with a large number of sessions, we will be passing
      // sessionId of the (un)selected session to loadCapacity(), for events havng # of sessions greater
      // than the SESSION_THRESHOLD value, so that only the (un)selected session's capacity is loaded along
      // with that of all the admisison items, regTypes, and the event itself. These will then be merged
      // along with the rest of the sessions' capacities.
      const sessionCount = getAllSessionCapacityIds(event).length;
      if (isSessionSelectionUpdate && (sessionCount > SESSION_THRESHOLD)) {
        const regPackId = getRegPackId(getState());
        await Promise.all([dispatch(loadCapacity(productId, regPackId)), dispatch(calculatePricing())]);
      } else {
        await Promise.all([dispatch(loadAvailableCapacityCounts()), dispatch(calculatePricing())]);
      }
      LOG.debug('loadAvailableCapacityCounts,calculatePricing success');
    } catch (error) {
      LOG.info('applyPartialEventRegistrationUpdate failed', error);
      dispatch({ type: UPDATE_REG_CART_FAILURE, payload: { error } });
      dispatch(hideLoadingOnError());
      if (getUpdateErrors.isProductAvailabilityError(error)) {
        dispatch(loadAvailableCapacityCounts());
        return await dispatch(openCapacityReachedDialog());
      } else if (getUpdateErrors.isEventClosed(error)) {
        return await dispatch(openPrivateEventErrorDialog());
      } else if (getUpdateErrors.isSessionOverlapError(error)) {
        return;
      }
      throw error;
    }
  };
}
